/* eslint-disable @typescript-eslint/no-unused-vars */
import { useLazyQuery } from '@apollo/client'
import { Button, Collapse, Hidden } from '@material-ui/core'
import FilterListIcon from '@material-ui/icons/FilterList'
import { Pagination } from '@material-ui/lab'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import PageHeader from '../../components/PageHeader'
import { TRANSACTIONS_QUERY } from '../../graphql/walletTransactions'
import { getTranslate } from '../../localization'
import { SortBy } from '../../types/walletEnums'
import {
  TransactionFilterInput,
  TransactionResponse
} from '../../types/walletTransaction'
import { TransactionHistoryFilterBox } from './TransactionHistoryFilters'
import { WalletTransactionListTableDesktop } from './WalletTransactionListTableDesktop'
import WalletTransactionSupportMobile from './WalletTransactionSupportMobile'
import useStyles from './styles/index.styles'

type Props = {}

export default function WalletTransactionHistory({}: Props) {
  const classes = useStyles()
  const params = useParams<{ walletId: string }>()

  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(false)
  const perPage = 6
  const [page, setpage] = useState<number>(1)
  const [filters, setfilters] = useState<TransactionFilterInput>({
    walletID: params.walletId,
    page: page,
    perPage: perPage,
    includeFundingMode: true
  })
  const [appliedFilters, setAppliedFilters] = useState(filters)
  const [sortBy, setsortBy] = useState<SortBy>('dectime')
  const [
    getTransactions,
    {
      data: transactionsData,
      loading: loadingForTransactions,
      error: transactionsError,
      variables: transactionVariables
    }
  ] = useLazyQuery<{
    transactions: TransactionResponse
  }>(TRANSACTIONS_QUERY, {
    notifyOnNetworkStatusChange: true
  })
  const applyFilters = (next: TransactionFilterInput) => {
    const scoped = {
      ...next,
      walletID: params.walletId,
      includeFundingMode: true
    }
    setfilters(scoped)
    setAppliedFilters(scoped)
    setpage(scoped.page)
    getTransactions({ variables: { filters: scoped, sortBy } })
  }
  useEffect(() => {
    const currentFilters = {
      ...appliedFilters,
      walletID: params.walletId,
      page: 1
    }
    setfilters(currentFilters)
    setAppliedFilters(currentFilters)
    setpage(1)
    getTransactions({
      variables: {
        filters: currentFilters,
        sortBy: sortBy
      }
    })
  }, [sortBy, params.walletId])

  const supportAvailable =
    !loadingForTransactions &&
    !transactionsError &&
    transactionVariables?.filters?.walletID === params.walletId

  return (
    <div>
      <PageHeader title={getTranslate('Transactions History')} />
      <div className={classes.filterButtonContainer}>
        <Button
          size="small"
          aria-expanded={filterButtonSelected}
          aria-controls="wallet-transaction-filters"
          startIcon={<FilterListIcon />}
          variant="outlined"
          color="primary"
          onClick={() => setFilterButtonSelected(!filterButtonSelected)}>
          {getTranslate('Filter')}
        </Button>
      </div>
      <div id="wallet-transaction-filters">
        <Collapse in={filterButtonSelected}>
          <TransactionHistoryFilterBox
            active={filterButtonSelected}
            filters={filters}
            setfilters={setfilters}
            onApply={applyFilters}
            loadingForTransactions={loadingForTransactions}
          />
        </Collapse>
      </div>
      <Hidden smDown>
        <WalletTransactionListTableDesktop
          supportAvailable={supportAvailable}
          data={transactionsData?.transactions.transactions || []}
          setSort={setsortBy}
          sort={sortBy}
          onRefresh={() => applyFilters({ ...appliedFilters, page: 1 })}
          {...{
            loadingForTransactions
          }}
        />
      </Hidden>
      <Hidden mdUp>
        <WalletTransactionSupportMobile
          data={transactionsData?.transactions.transactions || []}
          loading={loadingForTransactions}
          failed={Boolean(transactionsError)}
          available={supportAvailable}
          onRetry={() => {
            getTransactions({
              variables: {
                filters: { ...appliedFilters, walletID: params.walletId },
                sortBy
              }
            })
          }}
        />
      </Hidden>
      <div className={classes.paginationContainer}>
        <Pagination
          count={
            transactionsData &&
            (transactionsData.transactions.total % perPage === 0
              ? transactionsData.transactions.total / perPage
              : Math.floor(transactionsData.transactions.total / perPage) + 1)
          }
          color="primary"
          page={page}
          onChange={(_, value) => {
            setpage(value)
            applyFilters({ ...appliedFilters, page: value })
          }}
        />
      </div>
    </div>
  )
}
